import { useEffect, useState } from 'react';
import {
  Paper,
  Toolbar,
  Stack,
  IconButton,
  Typography,
  Box,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { ArrowBack } from '@mui/icons-material';
import UndoIcon from '@mui/icons-material/Undo';
import { LoadingButton } from '@mui/lab';
import { useLocation, useNavigate } from 'react-router';
import LoadingMask from '@/components/shared/LoadingMask';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';
import {
  getManifest,
  updateManifest,
  createManifest,
  rejectManifest,
} from '@/actions/manifest';
import { useApi } from '@/providers/ApiContext';
import ManifestNameField from './ManifestNameField';
import ManifestDocumentTypeField from './ManifestDocumentTypeField';
import { useUser } from '@/providers/UserContext';
import { useDocTypes } from '@/providers/DocTypesContext';
import ManifestReferenceDocumentField from './ManifestReferenceDocumentField';
import ManifestReferenceDocumentTypeField from './ManifestReferenceDocumentTypeField';
import type { Manifest, Document, DocumentType, EntityId } from '@/types';

export default function AdminManifestForm(): React.JSX.Element {
  const location = useLocation();
  const api = useApi();
  const user = useUser();
  const doc_types = useDocTypes();
  const navigate = useNavigate();

  const [revision, setRevision] = useState<number>(0);
  const [pristine, setPristine] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [loading_manifest, setLoadingManifest] = useState<boolean>(true);
  const [yanking, setYanking] = useState<boolean>(false);
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [name_error, setNameError] = useState<string | boolean>(false);
  const [document_type, setDocumentType] = useState<DocumentType | undefined>(
    undefined,
  );
  const [reference_document_type_options, setReferenceDocumentTypeOptions] =
    useState<DocumentType[]>([]);
  const [reference_document_type, setReferenceDocumentType] =
    useState<DocumentType | null>(null);
  const [reference_document, setReferenceDocument] = useState<Document | null>(
    null,
  );
  const [api_error, setApiError] = useState<string | null>(null);
  const [confirm_dialog_open, setConfirmDialogOpen] = useState<boolean>(false);
  const sp = new URLSearchParams(location.search);
  const manifest_id = sp.get('manifest_id');
  const type = sp.get('type');

  useEffect(() => {
    if (user?.entity_id && !manifest && api) {
      if (manifest_id) {
        getManifest(api, manifest_id)
          .then((data) => {
            setManifest(data);
            setName(data?.name || null);
            setReferenceDocument(
              (data as Manifest & { reference_document?: Document | null })
                ?.reference_document || null,
            );
            setLoadingManifest(false);
          })
          .catch((error) => {
            console.log(error);
            navigate('/error', {
              state: {
                errorMessage: 'Failed to load manifest for editing',
                errorDetails:
                  (error as Error).message ||
                  'Unable to retrieve the manifest for editing. This could be due to network issues, server problems, or the manifest may no longer exist.',
                returnPath: `/admin/manifests/${manifest_id}`,
              },
            });
          });
      } else {
        setLoadingManifest(false);
      }
    }
  }, [api, user?.entity_id, manifest, manifest_id, navigate]);

  useEffect(() => {
    if (doc_types && !document_type) {
      if (manifest_id) {
        if (manifest) {
          const doc_type = doc_types.find(
            (doc_type) => doc_type.entity_id === manifest.document_type_id,
          );
          setDocumentType(doc_type);
        }
      } else if (type && type !== 'pending') {
        const doc_type = doc_types.find((doc_type) => doc_type.code === type);
        setDocumentType(doc_type);
      } else {
        const maxLevel = Math.max(
          ...doc_types.map((doc_type) => doc_type.level),
        );
        const filteredArray = doc_types.filter(
          (doc_type) => doc_type.level === maxLevel,
        );
        setDocumentType(filteredArray[0]);
      }
    }
  }, [document_type, type, manifest, manifest_id, doc_types]);

  useEffect(() => {
    setRevision((prevState) => prevState + 1);
  }, [
    manifest,
    doc_types,
    document_type,
    reference_document,
    reference_document_type,
  ]);

  useEffect(() => {
    if (document_type && doc_types) {
      const filteredArray = doc_types.filter(
        (doc_type) => doc_type.level < document_type.level,
      );
      setReferenceDocumentTypeOptions(filteredArray);
    }
  }, [document_type, doc_types]);

  const performSubmit = async (): Promise<void> => {
    if (!document_type || !name || !api) return;
    setLoading(true);

    try {
      let saved_manifest_id: EntityId | null = manifest_id;
      if (manifest && manifest_id) {
        const payload: {
          name?: string;
          document_type_id?: EntityId;
          reference_document_id?: EntityId;
        } = {};
        if (name) payload.name = name;
        if (document_type) payload.document_type_id = document_type.entity_id;
        if (reference_document)
          payload.reference_document_id = reference_document.entity_id;
        await updateManifest(api, manifest_id, payload);
      } else {
        const payload: {
          document_type_id: EntityId;
          name: string;
          reference_document_id?: EntityId;
        } = {
          document_type_id: document_type.entity_id,
          name,
        };
        if (reference_document)
          payload.reference_document_id = reference_document.entity_id;
        const created = await createManifest(api, payload);
        saved_manifest_id = created.entity_id;
      }
      if (saved_manifest_id) {
        navigate(`/admin/manifests/${saved_manifest_id}`, { replace: true });
      }
    } catch (error) {
      console.error('Error saving manifest:', error);
      const err = error as Error;
      setApiError(
        err.message ||
          'Unable to save the manifest. This could be due to network issues, server problems, or invalid data.',
      );
      setLoading(false);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    // Check if creating without reference document
    if (!manifest && !reference_document) {
      setConfirmDialogOpen(true);
      return;
    }

    await performSubmit();
  };

  const handleYank = async (): Promise<void> => {
    if (!manifest_id || !document_type || !api) return;
    setYanking(true);
    try {
      await rejectManifest(api, manifest_id);
      navigate({
        pathname: '/admin/manifests',
        search: `?type=${document_type.code}`,
      });
    } catch (error) {
      console.error('Error yanking manifest:', error);
      const err = error as Error;
      setApiError(
        err.message ||
          'Unable to yank the manifest. This could be due to network issues, server problems, or insufficient permissions.',
      );
    } finally {
      setYanking(false);
    }
  };

  if (loading_manifest) {
    return <LoadingMask />;
  }

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Paper sx={{ display: 'flex', width: '100%' }} elevation={2}>
          <Toolbar
            sx={{
              minHeight: '55px !important',
            }}
          >
            <Stack spacing={2} direction='row' alignItems='center'>
              <IconButton onClick={() => navigate(-1)} disabled={loading}>
                <ArrowBack />
              </IconButton>
              <Typography variant='h5'>
                {manifest ? `Edit ${manifest.name}` : `Create New Manifest`}
              </Typography>
            </Stack>
          </Toolbar>
        </Paper>
      </Grid>

      <Grid size={12}>
        <Box
          padding='15px'
          sx={{
            maxWidth: 600,
          }}
        >
          <Grid container spacing={2}>
            <Grid size={12}>
              <ManifestDocumentTypeField
                key={`document_type_${revision}`}
                value={document_type || null}
                setValue={(value) => setDocumentType(value || undefined)}
                setChanged={() => setPristine(false)}
                edit={Boolean(manifest)}
              />
            </Grid>
            {document_type && !Boolean(manifest) ? (
              <Grid size={12}>
                <ManifestReferenceDocumentTypeField
                  key={`reference_document_type_${revision}`}
                  options={reference_document_type_options}
                  value={reference_document_type}
                  setValue={setReferenceDocumentType}
                  edit={Boolean(manifest)}
                />
              </Grid>
            ) : undefined}
            {reference_document_type || Boolean(manifest) ? (
              <Grid size={12}>
                <ManifestReferenceDocumentField
                  key={`reference_document_${revision}`}
                  document_type_id={
                    reference_document_type?.entity_id ||
                    (
                      manifest as Manifest & {
                        reference_document?: { document_type_id?: EntityId };
                      }
                    )?.reference_document?.document_type_id ||
                    ''
                  }
                  value={reference_document}
                  setValue={(doc) => {
                    setReferenceDocument(doc);
                    if (doc && (!name || name === '')) {
                      setName(`${doc.doc_id || ''} - ${doc.name}`);
                      setNameError(false);
                    }
                  }}
                  setChanged={() => setPristine(false)}
                  edit={Boolean(manifest)}
                />
              </Grid>
            ) : undefined}

            <Grid size={12}>
              <ManifestNameField
                key={`name_${revision}`}
                value={name || ''}
                error={name_error}
                setValue={setName}
                setError={setNameError}
                setChanged={() => setPristine(false)}
              />
            </Grid>

            {manifest?.status === 'active' && (
              <Grid size={12}>
                <LoadingButton
                  variant='outlined'
                  color='error'
                  onClick={handleYank}
                  loading={yanking}
                  disabled={yanking}
                  startIcon={<UndoIcon />}
                >
                  Yank
                </LoadingButton>
              </Grid>
            )}
          </Grid>
        </Box>
      </Grid>

      <Grid size={12}>
        <Divider />
        <Toolbar>
          <Stack spacing={2} direction='row'>
            <LoadingButton
              variant='contained'
              onClick={handleSubmit}
              loading={loading}
              disabled={
                pristine ||
                Boolean(name_error) ||
                !Boolean(name) ||
                !Boolean(document_type)
              }
            >
              Submit
            </LoadingButton>

            <Button
              variant='outlined'
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              Cancel
            </Button>
          </Stack>
        </Toolbar>
      </Grid>

      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />

      <Dialog
        open={confirm_dialog_open}
        onClose={() => setConfirmDialogOpen(false)}
        aria-labelledby='confirm-dialog-title'
        aria-describedby='confirm-dialog-description'
      >
        <DialogTitle id='confirm-dialog-title'>
          Confirm Create Without Reference Document
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='confirm-dialog-description'>
            Manifests without reference documents will not be able to
            automatically generate checks. All manifest checks will need to be
            configured manually. Are you sure you wish to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialogOpen(false)}
            variant='outlined'
            color='inherit'
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              setConfirmDialogOpen(false);
              await performSubmit();
            }}
            variant='contained'
            color='primary'
            autoFocus
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
